import { Button, Modal, Paper, Stack, TextField } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDeleteReminderMutation, useUpsertReminderMutation } from '../../../../api/remindersApi';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectedUserIdSelector } from '../../../../selectors/userSelectors';
import { DatePicker } from '@mui/x-date-pickers';
import BookkeeperMentions from '../../../Common/BookkeeperMentions';
import { useBookkeeperMentionOptions } from '../../../../hooks/useBookkeeperMentionOptions.js';

/** Default form state */
const EMPTY_FORM = {
  scheduledAt: null,
  title: '',
  notes: ''
};

const RemindersModal = ({ status, setStatus }) => {
  const userId = useSelector(selectedUserIdSelector);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues: EMPTY_FORM, mode: 'onTouched' });

  const [isMentionOpen, setIsMentionOpen] = useState(false);

  const titleInputRef = useRef(null);

  const [upsertReminder] = useUpsertReminderMutation();
  const [deleteReminder] = useDeleteReminderMutation();

  const { data: bookkeeperOptions } = useBookkeeperMentionOptions();

  const { open, selectedReminder } = status;

  const [title, scheduledAt, notes] = watch(['title', 'scheduledAt', 'notes']);
  const isPastDate = Boolean(scheduledAt) && moment.utc(scheduledAt).isBefore(moment.utc(), 'day');
  const canSave = Boolean(title) && Boolean(scheduledAt) && Boolean(notes) && !isPastDate;

  const handleSaveReminder = (values) => {
    upsertReminder({
      ...values,
      userId,
      mentionedBookkeeperIds,
      ...(selectedReminder && { id: selectedReminder.id })
    });
    handleClose();
  };

  const handleDeleteReminder = () => {
    deleteReminder(selectedReminder.id);
    handleClose();
  };

  const handleClose = () => {
    setStatus({ open: false, selectedReminder: null });
    reset(EMPTY_FORM);
    setIsMentionOpen(false);
  };

  /** Update the title and toggle the mention dropdown when the user types an `@` */
  const handleSetTitle = (value) => {
    const lastChar = value.slice(-1);
    const charBeforeLast = value.slice(-2, -1);
    // Only show tag menu if the last character is an @ and the character before it is a space or empty.
    // This is to prevent the tag menu from showing when the user is typing out an email address.
    if (lastChar === '@' && (charBeforeLast === '' || charBeforeLast === ' ')) {
      setIsMentionOpen(true);
    } else {
      setIsMentionOpen(false);
    }
  };

  /** Focus on the text input when the mention dropdown is closed */
  useEffect(() => {
    if (!isMentionOpen && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isMentionOpen]);

  /** Hydrate the form when editing an existing reminder, otherwise reset to empty */
  useEffect(() => {
    if (selectedReminder) {
      reset({
        scheduledAt: selectedReminder.scheduledAt ? moment.utc(selectedReminder.scheduledAt) : null,
        title: selectedReminder.title || '',
        notes: selectedReminder.notes || ''
      });
    } else {
      reset(EMPTY_FORM);
    }
  }, [selectedReminder, reset]);

  /** IDs of bookkeepers who have been tagged/mentioned */
  const mentionedBookkeeperIds = useMemo(() => {
    return bookkeeperOptions.filter((option) => title.includes(`@${option.label}`)).map((option) => option.value);
  }, [title, bookkeeperOptions]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='Add Reminder'
      aria-describedby='Modal used to create a new reminder'
    >
      <Paper sx={{ p: 4, maxWidth: 800, maxHeight: 800, overflow: 'auto', margin: 'auto', marginTop: '10vh' }}>
        <Stack
          sx={{
            gap: 3
          }}
        >
          <Controller
            name='title'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.title}
                fullWidth
                inputRef={titleInputRef}
                variant='standard'
                label='Title'
                onChange={(e) => {
                  field.onChange(e);
                  handleSetTitle(e.target.value);
                }}
              />
            )}
          />
          <Controller
            name='scheduledAt'
            control={control}
            rules={{
              required: true,
              validate: (value) => !moment.utc(value).isBefore(moment.utc(), 'day')
            }}
            render={({ field }) => (
              <DatePicker
                {...field}
                timezone='UTC'
                disablePast
                label='Date'
                slotProps={{
                  textField: {
                    ...(errors.scheduledAt && { error: true }),
                    onBlur: field.onBlur
                  }
                }}
              />
            )}
          />
          <Controller
            name='notes'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.notes}
                fullWidth
                multiline
                minRows={15}
                maxRows={15}
                variant='outlined'
                label='Notes'
              />
            )}
          />
          <Stack
            direction='column'
            sx={{
              gap: 1.5
            }}
          >
            <Button disabled={!canSave} variant='contained' color='primary' onClick={handleSubmit(handleSaveReminder)}>
              Save
            </Button>
            {selectedReminder && (
              <Button variant='text' color='error' onClick={handleDeleteReminder}>
                Delete
              </Button>
            )}
          </Stack>
        </Stack>
        <BookkeeperMentions
          isOpen={isMentionOpen}
          setOpen={setIsMentionOpen}
          anchorEl={titleInputRef.current}
          onSetMention={(option) => setValue('title', title + option.label, { shouldValidate: true })}
          isOnModal={true}
        />
      </Paper>
    </Modal>
  );
};

export default RemindersModal;
